import { useCallback, useMemo, useState, type ReactNode } from 'react'
import {
  ChromeConfigProvider,
  type ChromeConfig,
  type ChromeLinkProps,
  type NewsletterButtonProps,
} from '@gjl/studio-chrome'

/**
 * How the hymnal wires itself into the shared chrome.
 *
 * Every link in the header and footer points at a MARKETING route (`/about`,
 * `/services/websites`, `/`), and this app's router owns only `/hymnal/*`. A
 * react-router `<Link>` would be intercepted here, match nothing, and drop the
 * visitor on the hymnal's own 404 — without a single network request, so the
 * marketing page sitting at that exact URL is never fetched. A plain `<a>` is
 * therefore not a fallback but the correct adapter: it performs a real
 * navigation into the other build.
 */
function CrossBuildLink({ href, children, ...rest }: ChromeLinkProps) {
  return (
    <a href={href} {...rest}>
      {children}
    </a>
  )
}

/** Fugue Lab App public subscribe endpoint. See studio/client/src/lib/newsletter.ts. */
const APP_URL = import.meta.env.VITE_APP_URL || 'https://fuguelab.app'
const ORG_SLUG = import.meta.env.VITE_FUGUE_ORG_SLUG || 'fuguelab'
const NEWSLETTER_ENDPOINT = `${APP_URL}/api/public/o/${ORG_SLUG}/subscribe`

/** The footer's Subscribe button. Same classes studio's default <Button> resolves to. */
function SubscribeButton({ children, ...rest }: NewsletterButtonProps) {
  return (
    <button
      {...rest}
      data-slot="button"
      className="bg-primary text-primary-foreground hover:bg-primary/90 inline-flex h-9 shrink-0 items-center justify-center gap-2 rounded-md px-4 py-2 text-sm font-medium whitespace-nowrap shadow-xs transition-all disabled:pointer-events-none disabled:opacity-50"
    >
      {children}
    </button>
  )
}

/**
 * Minimal message host. Studio routes these to its `FeedbackProvider` toasts;
 * that provider also owns a `confirm()` dialog this app has no use for, so the
 * hymnal renders the message inline instead of importing the whole thing.
 */
export function HymnalChromeProvider({ children }: { children: ReactNode }) {
  const [message, setMessage] = useState<{ text: string; tone: 'success' | 'error' } | null>(null)

  const notify = useCallback((text: string, tone: 'success' | 'error') => {
    setMessage({ text, tone })
  }, [])

  const config: ChromeConfig = useMemo(
    () => ({
      Link: CrossBuildLink,
      newsletter: { endpoint: NEWSLETTER_ENDPOINT, Button: SubscribeButton, notify },
    }),
    [notify]
  )

  return (
    <ChromeConfigProvider config={config}>
      {children}
      <div aria-live="polite" className="fixed right-4 bottom-4 z-100 flex w-full max-w-sm flex-col gap-2">
        {message && (
          <div
            className={
              message.tone === 'error'
                ? 'bg-card border-destructive/50 text-destructive flex items-start justify-between gap-3 rounded-xl border px-4 py-3 text-sm shadow-lg'
                : 'bg-card border-primary/50 text-primary flex items-start justify-between gap-3 rounded-xl border px-4 py-3 text-sm shadow-lg'
            }
          >
            <span>{message.text}</span>
            <button
              type="button"
              onClick={() => setMessage(null)}
              className="text-muted-foreground hover:text-foreground shrink-0 transition-colors"
              aria-label="Dismiss notification"
            >
              ×
            </button>
          </div>
        )}
      </div>
    </ChromeConfigProvider>
  )
}
